/**
 * Shared helpers, design tokens and edit dialog for shortcut tiles.
 * Used by the user Shortcuts (CockpitHome) and admin Shortcuts (AdminSettings).
 */
import React, { useMemo, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { NAV_ITEMS } from '../../config/navigation';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, ToggleButton, ToggleButtonGroup, Tooltip, IconButton,
} from '@mui/material';

export const T = {
  primary: '#526075',
  tertiary: '#635983',
  bg: '#f8f9fa',
  surface: '#ffffff',
  surfaceLow: '#f1f4f5',
  surfaceHigh: '#e5e9eb',
  text: '#2d3335',
  outline: '#767c7e',
  outlineVariant: '#adb3b5',
  shadow: '0 10px 30px rgba(45,51,53,0.06)',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

export const Icon = ({ name, fill, size = 20, sx = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: size,
      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 500, 'GRAD' 0, 'opsz' ${size}`,
      lineHeight: 1,
      ...sx,
    }}
  >
    {name}
  </span>
);

export const COLORS = [
  '#526075', '#635983', '#3b82f6', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#8b5cf6', '#06b6d4', '#64748b',
];

export const ICONS = [
  'link', 'public', 'bookmark', 'star', 'folder', 'description',
  'assignment', 'schedule', 'trending_up', 'attach_money', 'analytics',
  'campaign', 'mail', 'chat', 'video_camera_front', 'movie', 'photo_camera',
  'design_services', 'palette', 'edit_note', 'work', 'business',
  'cloud', 'code', 'terminal', 'database', 'storage',
];

export const safeUrl = (raw) => {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;
  if (v.startsWith('/')) return v; // interne route (bv. /weekplan)
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

export const isInternalUrl = (raw) => String(raw || '').trim().startsWith('/');

export const hostnameOf = (url) => {
  if (isInternalUrl(url)) return 'intern';
  try {
    const u = new URL(safeUrl(url));
    const host = u.hostname.replace(/^www\./, '');
    const parts = host.split('.');
    if (parts.length >= 2) return parts.slice(-2).join('.');
    return host;
  } catch {
    return '';
  }
};

export const faviconUrl = (url) => {
  if (isInternalUrl(url)) return null; // interne pagina heeft geen favicon — icoon-modus
  const host = hostnameOf(url);
  return host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : null;
};

// Verklein een geuploade afbeelding naar een klein vierkant PNG-dataURL (compact
// genoeg om als `icon`-veld op te slaan). 'contain' zodat niets vervormt.
export function resizeImageToDataUrl(file, size = 72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = size; canvas.height = size;
          const ctx = canvas.getContext('2d');
          const scale = Math.min(size / img.width, size / img.height);
          const w = img.width * scale, h = img.height * scale;
          ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          resolve(canvas.toDataURL('image/png'));
        } catch (e) { reject(e); }
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Gedeelde tegel-visual: het pictogram op een icoon-tegel. Icoon-modus krijgt de
// achtergrondkleur; favicon en geüploade afbeelding staan zónder achtergrond.
export function ShortcutVisual({ item, faviconFailed, onFaviconError, size = 38 }) {
  const mode = item.mode || 'favicon';
  const isImg = mode === 'image' && item.icon;
  const isIcn = mode === 'icon' || (mode === 'favicon' && faviconFailed);
  const fav = faviconUrl(item.url);
  const bg = isIcn ? (item.color || T.primary) : 'transparent';
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '0.55rem', flexShrink: 0, overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: bg, color: isIcn ? '#fff' : T.text,
    }}>
      {isImg ? (
        <img src={item.icon} alt="" width={Math.round(size * 0.86)} height={Math.round(size * 0.86)} style={{ objectFit: 'contain', display: 'block' }} />
      ) : isIcn ? (
        <Icon name={item.icon || 'link'} size={Math.round(size * 0.53)} fill />
      ) : fav ? (
        <img src={fav} alt="" width={22} height={22} style={{ display: 'block' }} onError={onFaviconError} />
      ) : (
        <Icon name="link" size={Math.round(size * 0.53)} />
      )}
    </Box>
  );
}

export function ShortcutDialog({ editing, onChange, onSave, onCancel, title }) {
  const isOpen = !!editing;
  const previewHost = useMemo(() => hostnameOf(editing?.url || ''), [editing?.url]);
  const fileRef = useRef(null);

  if (!isOpen) return null;

  const mode = editing.mode || 'favicon';
  const update = (patch) => onChange({ ...editing, ...patch });
  const headerLabel = title || (editing?.isNew ? 'Nieuwe shortcut' : 'Shortcut bewerken');

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { update({ mode: 'image', icon: await resizeImageToDataUrl(f) }); }
    catch { /* negeren */ }
  };

  return (
    <Dialog open={isOpen} onClose={onCancel} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '0.85rem' } }}
    >
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800, color: T.text, pb: 1 }}>
        {headerLabel}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
        <Box sx={{
          bgcolor: T.surfaceLow, borderRadius: '0.6rem', p: 2,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <ShortcutVisual item={editing} size={40} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 700, color: T.text }}>
              {editing.name || 'Naam shortcut'}
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
              {previewHost || 'domein'}
            </Typography>
          </Box>
        </Box>

        <TextField
          label="Naam" size="small" autoFocus
          value={editing.name || ''} onChange={(e) => update({ name: e.target.value })}
          inputProps={{ maxLength: 40 }}
        />
        <Autocomplete
          size="small"
          options={NAV_ITEMS.map((i) => ({ label: i.label, path: i.path }))}
          getOptionLabel={(o) => o.label || ''}
          isOptionEqualToValue={(o, v) => o.path === v.path}
          value={NAV_ITEMS.map((i) => ({ label: i.label, path: i.path })).find((o) => o.path === editing.url) || null}
          onChange={(_e, val) => { if (val) update({ url: val.path, name: editing.name || val.label }); }}
          renderInput={(params) => (
            <TextField {...params} label="Interne tool (optioneel)" size="small"
              placeholder="Kies een pagina uit de app…" />
          )}
        />
        <TextField
          label="URL" size="small" placeholder="https://example.com of /weekplan (intern)"
          value={editing.url || ''} onChange={(e) => update({ url: e.target.value })}
        />

        <Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.06em', color: T.outline, mb: 1 }}>
            Weergave
          </Typography>
          <ToggleButtonGroup
            exclusive size="small" value={mode}
            onChange={(_, v) => v && update({ mode: v })}
            sx={{ '& .MuiToggleButton-root': { fontFamily: T.fontBody, textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' } }}
          >
            <ToggleButton value="favicon">Favicon</ToggleButton>
            <ToggleButton value="icon">Icoon</ToggleButton>
            <ToggleButton value="image">Afbeelding</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {mode === 'icon' && (
        <Box>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.06em', color: T.outline, mb: 1 }}>
            Kleur
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Tooltip title="Geen" arrow>
              <Box onClick={() => update({ color: null })} sx={{
                width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', bgcolor: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: !editing.color ? `2px solid ${T.text}` : `1px solid ${T.surfaceHigh}`,
                transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.1)' },
              }}><Icon name="block" size={14} sx={{ color: T.outlineVariant }} /></Box>
            </Tooltip>
            {COLORS.map(c => (
              <Box key={c} onClick={() => update({ color: c })} sx={{
                width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                border: editing.color === c ? `2px solid ${T.text}` : '2px solid transparent',
                transition: 'transform 0.1s', '&:hover': { transform: 'scale(1.1)' },
              }} />
            ))}
          </Box>
        </Box>
        )}

        {mode === 'icon' && (
          <Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.06em', color: T.outline, mb: 1 }}>
              Icoon
            </Typography>
            <Box sx={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', gap: 0.75,
              maxHeight: 200, overflowY: 'auto', p: 0.5,
              border: `1px solid ${T.surfaceHigh}`, borderRadius: '0.5rem',
            }}>
              {ICONS.map(name => (
                <Box key={name} onClick={() => update({ icon: name })} sx={{
                  width: 36, height: 36, borderRadius: '0.4rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: editing.icon === name ? (editing.color || T.primary) : 'transparent',
                  color: editing.icon === name ? '#fff' : T.text, transition: 'all 0.1s',
                  '&:hover': { bgcolor: editing.icon === name ? (editing.color || T.primary) : T.surfaceLow },
                }}>
                  <Icon name={name} size={18} fill={editing.icon === name} />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {mode === 'image' && (
          <Box>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.06em', color: T.outline, mb: 1 }}>
              Eigen afbeelding
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()}
                sx={{ fontFamily: T.fontBody, textTransform: 'none', fontWeight: 600 }}>
                {editing.mode === 'image' && editing.icon ? 'Vervangen' : 'Uploaden'}
              </Button>
              {editing.mode === 'image' && editing.icon && (
                <Tooltip title="Verwijderen" arrow>
                  <IconButton size="small" onClick={() => update({ icon: null })}><Icon name="close" size={16} /></IconButton>
                </Tooltip>
              )}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
                Klein PNG/JPG — wordt automatisch verkleind.
              </Typography>
            </Box>
            <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} style={{ display: 'none' }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} sx={{ fontFamily: T.fontBody, textTransform: 'none', color: T.outline }}>
          Annuleren
        </Button>
        <Button
          onClick={onSave}
          disabled={!editing.url?.trim()}
          variant="contained"
          sx={{
            fontFamily: T.fontBody, textTransform: 'none', fontWeight: 700,
            bgcolor: T.primary, '&:hover': { bgcolor: '#465469' },
          }}
        >
          Opslaan
        </Button>
      </DialogActions>
    </Dialog>
  );
}
